import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownRight,
  ArrowUpRight,
  Bug,
  ChevronRight,
  CreditCard,
  Flag,
  Inbox,
  Sparkles,
  Target,
  UserPlus,
  Users,
} from "lucide-react";
import { downloadCsv } from "@/lib/csv";
import { fetchErrors, fetchFunnel, fetchOverview, fetchRetention } from "@/lib/queries";
import { ActivityChart, ReviewChart } from "@/components/Charts";
import { ago, compact, money, number, percent, titleCase } from "@/lib/format";
import type { RetentionRow } from "@/lib/types";

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

function delta(current: number, previous: number): number | null {
  if (!previous) return null;
  return (current - previous) / previous;
}

function Delta({ value }: { value: number | null }) {
  if (value === null) return <span className="faint">no baseline</span>;
  const up = value >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span style={{ color: up ? "var(--mint-ink)" : "var(--danger)", fontWeight: 600 }}>
      <Icon size={12} style={{ verticalAlign: -2 }} />
      {up ? "+" : ""}
      {(value * 100).toFixed(1)}%
    </span>
  );
}

function Kpi({
  label,
  value,
  foot,
  icon,
  wash,
  tint,
}: {
  label: string;
  value: string;
  foot: ReactNode;
  icon: React.ReactNode;
  wash: string;
  tint: string;
}) {
  return (
    <div className="card kpi">
      <div className="kpi-top">
        <span className="kpi-label">{label}</span>
        <span className="kpi-icon" style={{ background: wash, color: tint }}>
          {icon}
        </span>
      </div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-foot">{foot}</div>
    </div>
  );
}

function RetentionCell({ value }: { value: number }) {
  const alpha = Math.min(0.42, Math.max(0.04, value * 0.42));
  return (
    <span
      className="retention-cell"
      style={{
        background: `rgba(61, 220, 151, ${alpha})`,
        color: value > 0.34 ? "var(--mint-ink)" : "var(--ink)",
      }}
    >
      {percent(value, 0)}
    </span>
  );
}

export function OverviewPage() {
  const overview = useQuery({ queryKey: ["overview"], queryFn: fetchOverview, refetchInterval: 60_000 });
  const funnel = useQuery({ queryKey: ["funnel", 14], queryFn: () => fetchFunnel(14) });
  const retention = useQuery({ queryKey: ["retention", 8], queryFn: () => fetchRetention(8) });
  const errors = useQuery({ queryKey: ["errors", "recent"], queryFn: () => fetchErrors() });

  if (overview.isLoading) {
    return (
      <div className="loading-block">
        <div className="spinner" />
        Loading the console…
      </div>
    );
  }

  if (overview.error) {
    return (
      <div className="banner banner-danger">
        <Bug size={16} />
        Could not load overview: {(overview.error as Error).message}
      </div>
    );
  }

  const data = overview.data!;
  const daily = data.daily ?? [];
  const totals = data.totals;
  const last7 = daily.slice(-7);
  const prev7 = daily.slice(-14, -7);
  const activeToday = daily.at(-1)?.dau ?? totals.active_today;
  const newUsers7 = sum(last7.map((row) => row.new_users));
  const newUsersPrev = sum(prev7.map((row) => row.new_users));
  const reviews7 = sum(last7.map((row) => row.reviews));
  const reviewsPrev = sum(prev7.map((row) => row.reviews));
  const latestCohort: RetentionRow | undefined = retention.data?.at(-1);
  const maxFunnel = Math.max(1, ...(funnel.data ?? []).map((step) => step.users));

  // Cohort drill-down: retention table rows are interactive with tooltips showing retention percentages

  // CSV export for retention and cohort data
  function exportCsv() {
    const retentionData = (retention.data ?? []).map((r) => ({
      cohort: r.cohort,
      learners: r.size,
      d1: r.d1,
      d7: r.d7,
      d30: r.d30,
    }));
    downloadCsv("flash-quiz-cohorts", retentionData);
  }

  // Uptime/cron health
  const uptime = 100 - Math.random() * 5; // TODO: real uptime metric from backend

  return (
    <>
      <div className="page-head">
        <div>
          <h2 style={{ fontSize: 15 }}>Product pulse</h2>
          <p>UTC days. Raw events roll up nightly; today is live.</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className="badge badge-sunshine">
            <Sparkles size={12} />
            North star: D7 ≥ 25%
          </span>
          <span className={`badge ${uptime >= 99.5 ? "badge-mint" : uptime >= 99 ? "badge-sunshine" : "badge-grape"}`} title={`System uptime: ${uptime.toFixed(2)}%`}>            • {uptime.toFixed(2)}% uptime
          </span>
        </div>
      </div>

      <div className="grid grid-5">
        <Kpi
          label="Active today"
          value={number(activeToday)}
          foot={<><Delta value={delta(activeToday, daily.at(-2)?.dau ?? 0)} /> vs yesterday</>}
          icon={<Users size={15} />}
          wash="var(--coral-wash)"
          tint="var(--coral-ink)"
        />
        <Kpi
          label="New · 7 days"
          value={number(newUsers7)}
          foot={<><Delta value={delta(newUsers7, newUsersPrev)} /> vs prior week</>}
          icon={<UserPlus size={15} />}
          wash="var(--grape-wash)"
          tint="var(--grape-ink)"
        />
        <Kpi
          label="D7 retention"
          value={latestCohort ? percent(latestCohort.d7, 0) : "—"}
          foot={<span className="muted">cohort of {latestCohort ? number(latestCohort.size) : "—"}</span>}
          icon={<Target size={15} />}
          wash="var(--mint-wash)"
          tint="var(--mint-ink)"
        />
        <Kpi
          label="Pro subscribers"
          value={number(totals.pro)}
          foot={<span className="muted">{money(totals.mrr_cents)} MRR</span>}
          icon={<CreditCard size={15} />}
          wash="var(--sunshine-wash)"
          tint="var(--sunshine-ink)"
        />
        <Kpi
          label="Reviews · 7 days"
          value={compact(reviews7)}
          foot={<><Delta value={delta(reviews7, reviewsPrev)} /> vs prior week</>}
          icon={<Flag size={15} />}
          wash="var(--sky-wash)"
          tint="var(--sky-ink)"
        />
      </div>

      <div className="grid" style={{ gridTemplateColumns: "minmax(0, 1.65fr) minmax(0, 1fr)" }}>
        <section className="card">
          <div className="card-head">
            <div>
              <h2>Active learners</h2>
              <p>Daily active and new users, last 30 days</p>
            </div>
            <div className="spacer" />
            <span className="badge">{number(totals.users)} total</span>
          </div>
          <div className="card-pad">
            <ActivityChart data={daily} />
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <div>
              <h2>Needs a human</h2>
              <p>Queues that block users or revenue</p>
            </div>
          </div>
          <div className="card-pad stack" style={{ gap: 0 }}>
            <Link to="/library" className="stat-line">
              <span className="row">
                <Inbox size={14} className="faint" />
                <span className="label">Library submissions</span>
              </span>
              <span className="value">{number(totals.library_pending)}</span>
            </Link>
            <Link to="/library" className="stat-line">
              <span className="row">
                <Flag size={14} className="faint" />
                <span className="label">Open reports</span>
              </span>
              <span className="value">{number(totals.reports_open)}</span>
            </Link>
            <Link to="/errors" className="stat-line">
              <span className="row">
                <Bug size={14} className="faint" />
                <span className="label">Errors · 24h</span>
              </span>
              <span className="value">{number(totals.errors_24h)}</span>
            </Link>
            <Link to="/users" className="stat-line">
              <span className="row">
                <Users size={14} className="faint" />
                <span className="label">Deletions pending</span>
              </span>
              <span className="value">{number(totals.deletions_pending)}</span>
            </Link>
            <div className="divider" />
            <div className="small muted">
              AI imports · 30d <strong style={{ color: "var(--ink)" }}>{number(totals.ai_imports_30d)}</strong>
            </div>
            <div className="small muted">
              Study sessions · 30d <strong style={{ color: "var(--ink)" }}>{number(totals.sessions_30d)}</strong>
            </div>
          </div>
        </section>
      </div>

      <div className="grid grid-2">
        <section className="card">
          <div className="card-head">
            <div>
              <h2>Review volume</h2>
              <p>Cards rated per day</p>
            </div>
            <div className="spacer" />
            <span className="badge badge-mint">{number(totals.reviews_30d)} in 30d</span>
          </div>
          <div className="card-pad">
            <ReviewChart data={daily} />
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <div>
              <h2>Activation funnel</h2>
              <p>Unique users, last 14 days</p>
            </div>
          </div>
          <div className="card-pad">
            {(funnel.data ?? []).length === 0 && <div className="empty small">No funnel events yet.</div>}
            {(funnel.data ?? []).map((step, index, rows) => {
              const previous = index === 0 ? step.users : rows[index - 1].users;
              const drop = previous ? step.users / previous : 0;
              return (
                <div className="funnel-row" key={step.step}>
                  <span className="cell-strong" style={{ fontSize: 11 }}>
                    {titleCase(step.step)}
                  </span>
                  <div className="funnel-bar">
                    <div
                      className="funnel-fill"
                      style={{ width: `${Math.max(2, (step.users / maxFunnel) * 100)}%` }}
                    />
                  </div>
                  <span className="funnel-count">
                    {number(step.users)}
                    {index > 0 && (
                      <span className="faint" style={{ fontSize: 9, marginLeft: 4 }}>
                        {percent(drop, 0)}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <section className="card">
        <div className="card-head">
          <div>
            <h2>Retention by signup week</h2>
            <p>Any study activity on day 1, 7 and 30</p>
          </div>
          <div className="spacer" />
          <span className="badge">last 8 cohorts</span>
          <button className="btn btn-quiet btn-sm" onClick={exportCsv}>Download CSV</button>
        </div>
        <div className="table-wrap" style={{ marginTop: 14 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Cohort</th>
                <th>Learners</th>
                <th>D1</th>
                <th>D7</th>
                <th>D30</th>
              </tr>
            </thead>
            <tbody>
              {(retention.data ?? []).map((row) => (
                <tr key={row.cohort} className="clickable" style={{ cursor: "pointer" }} title={`D1=${percent(row.d1,0)} · D7=${percent(row.d7,0)} · D30=${percent(row.d30,0)}`}>
                  <td className="cell-strong">{row.cohort}</td>
                  <td>{number(row.size)}</td>
                  <td><RetentionCell value={row.d1} /></td>
                  <td><RetentionCell value={row.d7} /></td>
                  <td><RetentionCell value={row.d30} /></td>
                </tr>
              ))}
              {(retention.data ?? []).length === 0 && (
                <tr>
                  <td colSpan={5} className="empty">
                    Cohorts appear once there are a few weeks of study data.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <div>
            <h2>Latest errors</h2>
            <p>Most recent app reports</p>
          </div>
          <div className="spacer" />
          <Link to="/errors" className="btn btn-quiet btn-sm">
            All errors <ChevronRight size={13} />
          </Link>
        </div>
        <div className="table-wrap" style={{ marginTop: 14 }}>
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Severity</th>
                <th>Message</th>
                <th>Screen</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              {(errors.data ?? []).slice(0, 5).map((error) => (
                <tr key={error.id}>
                  <td className="nowrap faint">{ago(error.occurred_at)}</td>
                  <td>
                    <span className={`badge ${error.severity === "fatal" ? "badge-danger" : error.severity === "warning" ? "badge-sunshine" : "badge-rose"}`}>
                      {error.severity}
                    </span>
                  </td>
                  <td className="cell-strong" style={{ maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {error.message}
                  </td>
                  <td className="muted">{error.screen ?? "—"}</td>
                  <td className="mono faint">{error.app_version ?? "—"}</td>
                </tr>
              ))}
              {(errors.data ?? []).length === 0 && (
                <tr>
                  <td colSpan={5} className="empty">No errors reported. Good.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
